import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ScrapedCategoryTable } from "@/components/scraped-category/table";
import { useGetScrapeCategories } from "@/hooks/api/scrape-category/useGetScrapeCategories";
import { AppPage } from "@/layouts/page";

export const ScrapeCategoriesPage = () => {
  let [searchParams, setSearchParams] = useSearchParams();

  const initialPage = searchParams.get("page")
    ? parseInt(searchParams.get("page")!)
    : 1;
  const initialLimit = searchParams.get("limit")
    ? parseInt(searchParams.get("limit")!)
    : 10;

  const [pageParams, setPageParams] = useState({
    page: initialPage,
    limit: initialLimit,
  });

  const [nameFilter, setNameFilter] = useState(searchParams.get("name") || "");
  const [supplierIdFilter, setSupplierIdFilter] = useState(
    searchParams.get("supplierId") || "",
  );
  const [blacklistFilter, setBlacklistFilter] = useState(
    searchParams.get("blacklistFilter") || "",
  );

  const { data: scrapeCategories, isFetching: isScrapeCategoriesLoading } =
    useGetScrapeCategories({
      ...pageParams,
      name: nameFilter || undefined,
      supplierId: supplierIdFilter || undefined,
      blacklistOnly:
        blacklistFilter === "blacklisted"
          ? true
          : blacklistFilter === "whitelisted"
            ? false
            : undefined,
    });

  useEffect(() => {
    const urlPage = searchParams.get("page");
    const urlLimit = searchParams.get("limit");

    if (urlPage) {
      const page = parseInt(urlPage);

      setPageParams((prev) => ({ ...prev, page }));
    }
    if (urlLimit) {
      const limit = parseInt(urlLimit);

      setPageParams((prev) => ({ ...prev, limit }));
    }
  }, [searchParams]);

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleLimitChange = (newLimit: number) => {
    searchParams.set("limit", newLimit.toString());
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleNameChange = (newName: string) => {
    setNameFilter(newName);
    if (newName) {
      searchParams.set("name", newName);
    } else {
      searchParams.delete("name");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleSupplierIdChange = (newSupplierId: string) => {
    setSupplierIdFilter(newSupplierId);
    if (newSupplierId) {
      searchParams.set("supplierId", newSupplierId);
    } else {
      searchParams.delete("supplierId");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleBlacklistFilterChange = (value: string) => {
    setBlacklistFilter(value);
    if (value && value !== "all") {
      searchParams.set("blacklistFilter", value);
    } else {
      searchParams.delete("blacklistFilter");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  return (
    <AppPage
      subtitle={
        scrapeCategories ? (
          <>
            <span className="tabular-nums">
              {scrapeCategories.total.toLocaleString()}
            </span>{" "}
            categories scraped from suppliers
          </>
        ) : (
          "Manage categories scraped from suppliers"
        )
      }
      title="Scraped categories"
    >
      <ScrapedCategoryTable
        blacklistFilter={blacklistFilter}
        changePage={handlePageChange}
        handleBlacklistFilterChange={handleBlacklistFilterChange}
        handleLimitChange={handleLimitChange}
        handleSearchTermChange={handleNameChange}
        handleSupplierIdChange={handleSupplierIdChange}
        isLoading={isScrapeCategoriesLoading}
        items={scrapeCategories?.data ?? []}
        limit={pageParams.limit ?? 10}
        page={pageParams.page ?? 1}
        searchTerm={nameFilter}
        supplierId={supplierIdFilter}
        totalCategories={scrapeCategories?.total ?? 0}
        totalPage={scrapeCategories?.totalPages ?? 1}
      />
    </AppPage>
  );
};
